import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Alert, AlertDescription } from '../ui/alert';
import { Badge } from '../ui/badge';
import { AlertTriangle, FileText, Upload, Save, Loader2, History, ShieldCheck, RefreshCw, X, CornerDownLeft } from 'lucide-react';
import { NOMINATIVE_FIELD_KEYS, numericKindOf, parseFrNumber, formatFrNumber } from './docRectifConstants';
import { ControleTotalTerritoires } from './ControleTotalTerritoires';

export default function DocRectifView({ s }) {
  const { open, onClose, reference, domain, docTypesList, mode, setMode, documentType, setDocumentType, motif, setMotif, edits, setEdits, file, setFile, submitting, archives, currentValues, lastResult, fieldSearch, setFieldSearch, collapsedGroups, setCollapsedGroups, quickFilter, setQuickFilter, PREVIEW_DOC_TYPES, showSplitView, setShowSplitView, previewDocType, setPreviewDocType, originalPdfUrl, originalLoading, previewPdfUrl, previewLoading, previewError, fieldGroups, totalFields, handleEditField, fetchPreviewPdf, REQUESTER_OPTIONS, requester, setRequester, autoMotif, setAutoMotif, buildAutoMotif, canSubmit, submitBlockedReason, handleSubmit } = s;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className={`${showSplitView ? 'max-w-[95vw]' : 'max-w-3xl'} max-h-[95vh] overflow-y-auto transition-[max-width] duration-300`}
        data-testid="rectify-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-2 text-xl">
            <span className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-red-700" />
              Rectifier un document signe - <span className="font-mono text-sm">{reference}</span>
            </span>
            {mode === 'edit' && (
              <Button
                type="button"
                size="sm"
                variant={showSplitView ? 'default' : 'outline'}
                onClick={() => setShowSplitView((v) => !v)}
                className={showSplitView ? 'bg-blue-600 hover:bg-blue-700 text-white text-xs h-8' : 'text-xs h-8 border-blue-300 text-blue-700 hover:bg-blue-50'}
                data-testid="toggle-splitview-btn"
              >
                <FileText className="h-3.5 w-3.5 mr-1" />
                {showSplitView ? 'Masquer l\'apercu' : 'Comparer cote a cote'}
              </Button>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className={showSplitView ? 'grid grid-cols-1 lg:grid-cols-2 gap-4' : ''}>
          <div className="space-y-4">
        <Alert className="border-amber-300 bg-amber-50">
          <AlertTriangle className="h-4 w-4 text-amber-700" />
          <AlertDescription className="text-sm text-amber-900">
            <strong>Acte juridique sensible.</strong> La version signee originale sera archivee
            dans la GED (consultable indefiniment). Un bandeau rouge "DOCUMENT RECTIFIE" sera appose
            sur le nouveau PDF. La <strong>preuve eIDAS RFC 3161 ORIGINALE reste inchangee</strong> :
            elle continue de certifier la version signee archivee.
          </AlertDescription>
        </Alert>

        {domain === 'domium' && (
          <Alert className="border-blue-200 bg-blue-50">
            <AlertDescription className="text-xs text-blue-900 leading-relaxed">
              <strong className="text-blue-800">Guide rapide :</strong>{' '}
              <strong>Particulier</strong> → renseignez{' '}
              <code className="bg-blue-100 px-1 rounded">civilite, nom, prenom, adresse, code_postal, ville, telephone, email</code>{' '}
              (ignorez raison_sociale, SIRET, forme_juridique, code_naf, representant_*).{' '}
              <strong>Entreprise / Promoteur</strong> → renseignez aussi{' '}
              <code className="bg-blue-100 px-1 rounded">raison_sociale, forme_juridique, SIRET, code_naf, representant_*</code>.{' '}
              Astuce : tapez <em>"adresse"</em> dans le filtre pour cibler instantanement.
            </AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="doc-type">Type de document</Label>
            <Select value={documentType} onValueChange={setDocumentType}>
              <SelectTrigger data-testid="rectify-doctype" id="doc-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {docTypesList.map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="mode">Mode</Label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger data-testid="rectify-mode" id="mode">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="edit">Edition des champs + regeneration</SelectItem>
                <SelectItem value="upload">Remplacement par PDF charge</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Bloc Motif avec sélecteur "A la demande de" + auto-redaction (universel domium + rcds) */}
        <div className="border rounded-lg p-3 bg-slate-50 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="requester" className="text-xs font-semibold text-slate-700">
                  A la demande de
                </Label>
                <Select value={requester} onValueChange={setRequester}>
                  <SelectTrigger id="requester" className="h-9 mt-1 bg-white" data-testid="rectify-requester">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {REQUESTER_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col justify-end">
                <label className="inline-flex items-center gap-2 text-xs text-slate-700 cursor-pointer bg-white border rounded-md px-3 h-9">
                  <input
                    type="checkbox"
                    checked={autoMotif}
                    onChange={(e) => setAutoMotif(e.target.checked)}
                    className="rounded border-slate-300"
                    data-testid="rectify-auto-motif"
                  />
                  <span>
                    <strong>Redaction auto du motif</strong>
                    <span className="text-slate-500 ml-1">(synchro avec les champs modifies)</span>
                  </span>
                </label>
              </div>
          </div>

          <div>
            <Label htmlFor="motif" className="flex items-center gap-1 text-sm font-semibold">
              Motif <span className="text-red-600">*</span>
              <span className="ml-1 text-xs font-normal text-gray-500">(min. 10 caracteres, trace dans l'audit_trail)</span>
            </Label>
            <Textarea
              id="motif"
              value={motif}
              onChange={(e) => {
                setMotif(e.target.value);
                // Si l'utilisateur edite manuellement, on coupe l'auto-sync
                if (autoMotif) setAutoMotif(false);
              }}
              placeholder="Ex: correction adresse de chantier suite remarque notariale du 12 mai 2026"
              rows={2}
              className={`resize-none mt-1 bg-white ${
                motif.trim().length === 0
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                  : motif.trim().length < 10
                    ? 'border-amber-400 focus:border-amber-500 focus:ring-amber-500'
                    : 'border-emerald-400 focus:border-emerald-500 focus:ring-emerald-500'
              }`}
              data-testid="rectify-motif"
            />
            <div className="flex items-center justify-between mt-1">
              <div className={`text-xs font-medium ${
                motif.trim().length === 0
                  ? 'text-red-600'
                  : motif.trim().length < 10
                    ? 'text-amber-700'
                    : 'text-emerald-700'
              }`}>
                {motif.trim().length === 0
                  ? '⚠ Motif obligatoire pour activer le bouton Rectifier'
                  : motif.trim().length < 10
                    ? `${motif.trim().length} / 10 minimum — encore ${10 - motif.trim().length} caractere(s)`
                    : `✓ ${motif.trim().length} caractere(s) — motif valide`}
              </div>
              {!autoMotif && Object.keys(edits).length > 0 && (
                <button
                  type="button"
                  onClick={() => { setAutoMotif(true); setMotif(buildAutoMotif()); }}
                  className="text-[11px] text-blue-700 hover:text-blue-900 underline"
                  data-testid="rectify-regen-motif"
                >
                  Regenerer automatiquement
                </button>
              )}
            </div>
          </div>
        </div>

        {mode === 'edit' && (
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-3">
              <Label className="text-sm font-semibold">
                Champs a modifier (laissez vide pour ne pas toucher)
              </Label>
              {Object.keys(currentValues).length > 0 && (
                <span className="text-[11px] text-gray-400 italic" data-testid="rectify-current-values-hint">
                  Texte gris = valeur actuelle du dossier
                </span>
              )}
              <div className="text-xs text-gray-500 flex items-center gap-2">
                <span>
                  {totalFields} champ(s) editable(s){' '}
                  {Object.keys(edits).length > 0 && (
                    <span className="text-amber-700 font-semibold">
                      · {Object.keys(edits).length} modifie(s)
                    </span>
                  )}
                </span>
                {Object.keys(edits).length > 0 && (
                  <button
                    type="button"
                    onClick={() => setEdits({})}
                    className="inline-flex items-center gap-1 text-[11px] text-red-700 hover:text-white hover:bg-red-600 border border-red-300 rounded px-2 py-0.5 transition-colors"
                    data-testid="rectify-clear-all"
                    title="Annuler toutes les modifications en attente"
                  >
                    <X className="h-3 w-3" /> Tout reinitialiser
                  </button>
                )}
              </div>
            </div>

            <Input
              type="search"
              placeholder="Filtrer les champs (ex : adresse, siret, chantier...)"
              value={fieldSearch}
              onChange={(e) => setFieldSearch(e.target.value)}
              className="h-8 text-sm"
              data-testid="rectify-field-search"
            />

            {/* V36.40 — Chips de filtre rapide par theme */}
            <div className="flex flex-wrap gap-1.5">
              {[
                { id: 'all', label: 'Tout', testid: 'qf-all' },
                { id: 'edited', label: '✎ Modifies', testid: 'qf-edited' },
                ...(domain !== 'rcds' ? [{ id: 'nominative', label: 'Attestation nominative', testid: 'qf-nominative' }] : []),
                { id: 'identite', label: 'Identite', testid: 'qf-identite' },
                { id: 'operation', label: 'Operation', testid: 'qf-operation' },
                { id: 'intervenants', label: 'Tableaux', testid: 'qf-tableaux' },
                { id: 'calcul', label: 'Calcul', testid: 'qf-calcul' },
                { id: 'articles', label: 'Articles', testid: 'qf-articles' },
                { id: 'sections', label: 'Sections', testid: 'qf-sections' },
              ].map((chip) => (
                <button
                  key={chip.id}
                  type="button"
                  onClick={() => setQuickFilter(chip.id)}
                  className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${
                    quickFilter === chip.id
                      ? 'bg-indigo-600 text-white border-indigo-700 shadow-sm'
                      : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-100'
                  }`}
                  data-testid={`rectify-quickfilter-${chip.testid}`}
                >
                  {chip.label}
                </button>
              ))}
            </div>

            <div className="border rounded p-3 bg-gray-50 max-h-[420px] overflow-y-auto space-y-3">
              {fieldGroups.map((group) => {
                const lowerSearch = fieldSearch.trim().toLowerCase();
                // V36.40 — Filtrage par chip de categorie (en plus de la recherche)
                const matchQuickFilter = (label, key, groupTitle) => {
                  if (quickFilter === 'all') return true;
                  if (quickFilter === 'edited') return edits[key] !== undefined;
                  if (quickFilter === 'nominative') return NOMINATIVE_FIELD_KEYS.includes(key);
                  const hay = `${label} ${key} ${groupTitle}`.toLowerCase();
                  if (quickFilter === 'identite') return /(identit|repr[ée]sentant|adresse entreprise|nom |pr[eé]nom|civilit|raison sociale|siret|forme juridique|code naf|t[ée]l[ée]phone|email|civilit)/i.test(hay);
                  if (quickFilter === 'operation') return /(operation|chantier|ouvrage|permis|cadastr|territoire|adresse|ville|destination|description|surface|reception|p[eé]rim[eè]tre)/i.test(hay);
                  if (quickFilter === 'intervenants') return /(intervenants|tableau|sous-traitant|maitre d'.uvre|maitre d.oeuvre|bet |architecte|controleur|sps|bureau)/i.test(hay);
                  if (quickFilter === 'calcul') return /(calcul|contribution|tarification|montant|plafond|franchise|coefficient|taux|remise|reduction|cout|prix)/i.test(hay);
                  if (quickFilter === 'articles') return /article\s*\d+/i.test(hay);
                  if (quickFilter === 'sections') return /(titre|section|bloc|paragraphe|pr[eé]ambule|intro|note de lecture|sommaire|garde|texte_overrides\.[^a]|sous-titre)/i.test(hay);
                  return true;
                };
                const visibleFields = group.fields.filter((f) => {
                  if (lowerSearch && !(f.label.toLowerCase().includes(lowerSearch) || f.key.toLowerCase().includes(lowerSearch))) return false;
                  return matchQuickFilter(f.label, f.key, group.title);
                });
                if (visibleFields.length === 0) return null;
                const isCollapsed = collapsedGroups[group.title];
                const editedCount = group.fields.filter((f) => edits[f.key] !== undefined).length;
                return (
                  <div key={group.title} className="bg-white rounded border border-gray-200">
                    <button
                      type="button"
                      onClick={() =>
                        setCollapsedGroups((prev) => ({ ...prev, [group.title]: !prev[group.title] }))
                      }
                      className="w-full flex items-center justify-between px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wide bg-gray-100 hover:bg-gray-200 rounded-t"
                      data-testid={`rectify-group-toggle-${group.title.replace(/\s+/g, '-').toLowerCase()}`}
                    >
                      <span className="flex items-center gap-2">
                        <span>{isCollapsed ? '▸' : '▾'}</span>
                        {group.title}
                        <span className="text-[10px] font-normal text-gray-500">
                          ({visibleFields.length}/{group.fields.length})
                        </span>
                      </span>
                      {editedCount > 0 && (
                        <Badge className="bg-amber-600 text-slate-800 text-white text-[10px] hover:bg-amber-100 font-semibold">
                          {editedCount} modifie(s)
                        </Badge>
                      )}
                    </button>
                    {!isCollapsed && (
                      <div className="p-3 grid grid-cols-1 gap-2">
                        {group.fields.some((f) => f.key.startsWith('territoires_ca.')) && (
                          <ControleTotalTerritoires edits={edits} currentValues={currentValues} />
                        )}
                        {visibleFields.map((f) => {
                          const isEdited = edits[f.key] !== undefined;
                          const hasCurrent = currentValues[f.key] !== undefined && currentValues[f.key] !== '';
                          // V37.91 — validation numérique en temps réel
                          const numKind = numericKindOf(f.key);
                          const parsed = isEdited && numKind && edits[f.key] !== '' ? parseFrNumber(edits[f.key]) : null;
                          const numState = parsed === null ? null : (Number.isNaN(parsed) ? 'invalid' : 'valid');
                          return (
                            <div key={f.key} className="flex items-start gap-2">
                              <Label
                                className={`text-xs w-64 shrink-0 font-mono pt-2 ${
                                  isEdited ? 'text-amber-700 font-semibold' : 'text-gray-600'
                                }`}
                                title={f.key}
                              >
                                {f.label}
                              </Label>
                              <div className="flex-1">
                                <div className="relative">
                                <Input
                                  value={edits[f.key] ?? ''}
                                  onChange={(e) => handleEditField(f.key, e.target.value)}
                                  onBlur={() => {
                                    if (!f.key.startsWith('territoires_ca.') || !edits[f.key]) return;
                                    const n = parseFrNumber(edits[f.key]);
                                    if (n !== null && !Number.isNaN(n)) handleEditField(f.key, formatFrNumber(n, 'money'));
                                  }}
                                  placeholder={hasCurrent ? String(currentValues[f.key]) : ''}
                                  title={
                                    hasCurrent
                                      ? `Valeur actuelle : ${String(currentValues[f.key])}`
                                      : 'Aucune valeur actuelle'
                                  }
                                  className={`h-8 text-sm ${
                                    numState === 'valid' ? 'border-emerald-500 bg-emerald-50 pr-8'
                                    : numState === 'invalid' ? 'border-red-400 bg-red-50 pr-8'
                                    : isEdited ? 'border-amber-400 bg-amber-50 pr-8' : hasCurrent ? 'pr-8' : ''
                                  }`}
                                  data-testid={`rectify-field-${f.key.replace(/\./g, '-')}`}
                                />
                                {isEdited && (
                                  <button
                                    type="button"
                                    onClick={() => handleEditField(f.key, '')}
                                    title={`Annuler la modification de "${f.label}"`}
                                    aria-label={`Annuler la modification de ${f.label}`}
                                    data-testid={`rectify-clear-${f.key.replace(/\./g, '-')}`}
                                    className="absolute right-1 top-1/2 -translate-y-1/2 inline-flex items-center justify-center h-6 w-6 rounded-full text-amber-700 hover:text-white hover:bg-red-500 transition-colors"
                                  >
                                    <X className="h-3.5 w-3.5" />
                                  </button>
                                )}
                                {!isEdited && hasCurrent && (
                                  <button
                                    type="button"
                                    onClick={() => handleEditField(f.key, String(currentValues[f.key]))}
                                    title="Préremplir avec la valeur actuelle"
                                    aria-label={`Préremplir ${f.label} avec la valeur actuelle`}
                                    data-testid={`rectify-prefill-${f.key.replace(/\./g, '-')}`}
                                    className="absolute right-1 top-1/2 -translate-y-1/2 inline-flex items-center justify-center h-6 w-6 rounded-full text-gray-400 hover:text-white hover:bg-violet-500 transition-colors"
                                  >
                                    <CornerDownLeft className="h-3.5 w-3.5" />
                                  </button>
                                )}
                                </div>
                                {numState === 'valid' && (
                                  <div className="text-[10px] text-emerald-600 mt-0.5 font-medium" data-testid={`rectify-numhint-${f.key.replace(/\./g, '-')}`}>
                                    ✓ {formatFrNumber(parsed, numKind)}
                                  </div>
                                )}
                                {numState === 'invalid' && (
                                  <div className="text-[10px] text-red-500 mt-0.5" data-testid={`rectify-numhint-${f.key.replace(/\./g, '-')}`}>
                                    Format numérique invalide (ex : 85 000,50)
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
              {(fieldSearch || quickFilter !== 'all') &&
                fieldGroups.every((g) => {
                  const lowerSearch = fieldSearch.toLowerCase();
                  return g.fields.filter((f) => {
                    if (lowerSearch && !(f.label.toLowerCase().includes(lowerSearch) || f.key.toLowerCase().includes(lowerSearch))) return false;
                    if (quickFilter === 'edited') return edits[f.key] !== undefined;
                    return true;  // approximation : si fieldSearch seul, conserve logique d'origine
                  }).length === 0;
                }) && (
                  <div className="text-center text-sm text-gray-500 py-4">
                    Aucun champ ne correspond
                    {fieldSearch ? ` a "${fieldSearch}"` : ''}
                    {quickFilter !== 'all' ? ` (filtre : ${quickFilter})` : ''}.
                  </div>
                )}
            </div>
            <div className="text-xs text-gray-500">
              {Object.keys(edits).length} champ(s) seront modifie(s)
            </div>
          </div>
        )}

        {mode === 'upload' && (
          <div className="space-y-2">
            <Label htmlFor="file" className="text-sm font-semibold">PDF correctif a charger (max 20 Mo)</Label>
            <Input
              id="file"
              type="file"
              accept="application/pdf"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="rectify-file-input"
            />
            {file && (
              <div className="text-xs text-emerald-700 flex items-center gap-1">
                <Upload className="h-3 w-3" />
                {file.name} ({(file.size / 1024).toFixed(1)} Ko)
              </div>
            )}
          </div>
        )}

        {archives.length > 0 && (
          <div className="border-t pt-3 mt-2">
            <Label className="text-sm font-semibold flex items-center gap-1 text-gray-700">
              <History className="h-4 w-4" />
              Versions deja archivees ({archives.length})
            </Label>
            <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
              {archives.map((a) => (
                <div key={a.id} className="text-xs flex items-center justify-between bg-gray-100 px-2 py-1 rounded">
                  <span className="flex items-center gap-2">
                    <Badge variant="outline" className="text-[10px]">v{a.rectification_version}</Badge>
                    <FileText className="h-3 w-3" />
                    <span className="font-mono">{a.rectification_archive_for || a.document_type}</span>
                  </span>
                  <span className="text-gray-500 truncate max-w-[50%]" title={a.rectification_motif}>
                    {a.rectification_motif}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {lastResult && (
          <Alert className="border-emerald-300 bg-emerald-50">
            <AlertDescription className="text-sm text-emerald-900">
              <strong>Succes.</strong> {lastResult.message}
              <div className="mt-1 text-xs font-mono">
                Nouvelle empreinte SHA-256 : {lastResult.rectified?.sha256?.slice(0, 16)}...
              </div>
            </AlertDescription>
          </Alert>
        )}
          </div>
          {/* Colonne droite : PDF split-view (DOMIUM uniquement) */}
          {showSplitView && (
            <div className="space-y-3 lg:sticky lg:top-0 lg:self-start">
              <div className="flex items-center justify-between gap-2 bg-slate-50 border rounded-lg p-2">
                <div className="flex items-center gap-2">
                  <Label className="text-xs font-semibold whitespace-nowrap">Document apercu :</Label>
                  <Select value={previewDocType} onValueChange={setPreviewDocType}>
                    <SelectTrigger className="h-8 text-xs w-[220px]" data-testid="preview-doctype-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PREVIEW_DOC_TYPES.map((d) => (
                        <SelectItem key={d} value={d} className="text-xs">
                          {d === 'attestation_definitive' && 'Attestation Definitive'}
                          {d === 'attestation_provisoire' && 'Attestation Provisoire'}
                          {d === 'attestation_nominative' && 'Attestation Nominative'}
                          {d === 'cp' && 'Conditions Particulieres'}
                          {d === 'cg' && 'Conditions Generales'}
                          {d === 'rcds_pack_unique' && 'Pack unifie RCDS'}
                          {d === 'rcds_attestation' && 'Attestation RCDS'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  type="button"
                  size="sm"
                  onClick={fetchPreviewPdf}
                  disabled={previewLoading || Object.keys(edits).length === 0}
                  className="h-8 text-xs bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="refresh-preview-btn"
                >
                  {previewLoading
                    ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                    : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
                  Actualiser l'apercu
                </Button>
              </div>

              {previewError && (
                <Alert className="border-red-200 bg-red-50 py-2">
                  <AlertDescription className="text-xs text-red-800">{previewError}</AlertDescription>
                </Alert>
              )}

              <div className="grid grid-cols-2 gap-2 h-[65vh]">
                {/* ORIGINAL */}
                <div className="flex flex-col border-2 border-slate-200 rounded-lg overflow-hidden">
                  <div className="px-3 py-1.5 bg-slate-100 text-xs font-semibold text-slate-700 border-b flex items-center gap-1">
                    <FileText className="h-3 w-3" />
                    Original
                  </div>
                  {originalLoading ? (
                    <div className="flex-1 flex flex-col items-center justify-center text-xs text-gray-600 p-4 text-center gap-2" data-testid="preview-original-loading">
                      <Loader2 className="h-5 w-5 animate-spin text-slate-500" />
                      <span>Génération du PDF en cours…</span>
                      <span className="text-[10px] text-gray-400">(attestation nominative : ~15-25 s)</span>
                    </div>
                  ) : originalPdfUrl ? (
                    <iframe
                      title="Original PDF"
                      src={originalPdfUrl}
                      className="flex-1 w-full"
                      data-testid="preview-original-iframe"
                    />
                  ) : (
                    <div className="flex-1 flex items-center justify-center text-xs text-gray-500 p-4 text-center">
                      Aucun PDF original (document non encore genere)
                    </div>
                  )}
                </div>
                {/* PREVIEW */}
                <div className="flex flex-col border-2 border-blue-300 rounded-lg overflow-hidden bg-blue-50">
                  <div className="px-3 py-1.5 bg-blue-100 text-xs font-semibold text-blue-800 border-b flex items-center gap-1">
                    <ShieldCheck className="h-3 w-3" />
                    Apercu corrige {Object.keys(edits).length > 0 && `(${Object.keys(edits).length} edit)`}
                  </div>
                  {previewPdfUrl ? (
                    <iframe
                      title="Preview PDF"
                      src={previewPdfUrl}
                      className="flex-1 w-full"
                      data-testid="preview-corrected-iframe"
                    />
                  ) : (
                    <div className="flex-1 flex flex-col items-center justify-center text-xs text-gray-500 p-4 text-center gap-2">
                      {Object.keys(edits).length === 0 ? (
                        <span>Modifiez d'abord un champ a gauche, puis cliquez "Actualiser l'apercu".</span>
                      ) : (
                        <>
                          <span>{Object.keys(edits).length} modification(s) en attente.</span>
                          <Button size="sm" type="button" onClick={fetchPreviewPdf} disabled={previewLoading}
                                  className="h-7 text-xs bg-blue-600 hover:bg-blue-700 text-white">
                            {previewLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <RefreshCw className="h-3 w-3 mr-1" />}
                            Generer l'apercu
                          </Button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <p className="text-[10px] text-gray-500 italic">
                L'apercu est genere en memoire, sans aucune ecriture en base. Cliquez "Rectifier" en bas pour appliquer reellement les modifications.
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2 sm:items-center">
          {!canSubmit() && !submitting && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 px-3 py-1.5 rounded-md flex items-center gap-1.5 mr-auto" data-testid="rectify-submit-blocked">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
              Pour activer "Rectifier" : <strong className="font-semibold">{submitBlockedReason()}</strong>
            </div>
          )}
          <Button variant="outline" onClick={onClose} disabled={submitting} data-testid="rectify-cancel">
            Fermer
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit() || submitting}
            className="bg-red-700 hover:bg-red-800 text-white disabled:bg-gray-400 disabled:hover:bg-gray-400"
            title={!canSubmit() ? `Bouton desactive : ${submitBlockedReason()}` : 'Appliquer la rectification (irreversible cote signature)'}
            data-testid="rectify-submit"
          >
            {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Rectifier (irreversible cote signature)
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
